import { yupResolver } from '@hookform/resolvers/yup'
import { LineTime } from '@qr-front/icons/line-time'
import { GENERATOR_FORM_ANIMATION_DURATION } from 'features/generator/components/GeneratorFormSection'
import { forwardRef, useImperativeHandle } from 'react'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import { validateContentForm } from 'utils/formUtils'

import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import { OpeningHoursFields } from './OpeningHoursFields'
import { getOpeningHoursSchema } from './openingHoursSchema'
import { openingHoursStateAtom } from './openingHoursState'
import { TOpeningHoursState } from './openingHoursTypes'
import { TabNavigation } from './TabNavigation'

export const OPENING_HOURS_FORM_ID = 'opening-hours'

export const OpeningHours = forwardRef<TContentFormHandle>((_, ref) => {
	const form = useForm<TOpeningHoursState>({
		defaultValues: getStore().get(openingHoursStateAtom),
		mode: 'onTouched',
		reValidateMode: 'onChange',
		shouldUnregister: false,
		resolver: yupResolver(
			getOpeningHoursSchema(),
		) as unknown as Resolver<TOpeningHoursState>,
	})

	const {
		handleSubmit,
		formState: { isValid },
	} = form

	useSyncFormSectionValidity({ formId: OPENING_HOURS_FORM_ID, isValid })

	useImperativeHandle(
		ref,
		() => ({
			validate: () =>
				validateContentForm(
					(onValid, onInvalid) => handleSubmit(onValid, onInvalid),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit],
	)

	return (
		<form className="flex flex-col gap-2">
			<FormProvider {...form}>
				<div className="flex flex-col gap-6 desktop:gap-8">
					<TabNavigation />
					<OpeningHoursFields />
				</div>
			</FormProvider>
		</form>
	)
})

OpeningHours.displayName = 'OpeningHours'

export const openingHoursFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: OPENING_HOURS_FORM_ID,
	title: t('generator__content_form_section__opening_hours__title'),
	description: t('generator__content_form_section__opening_hours__description'),
	icon: <LineTime />,
	Content: OpeningHours,
})
